import React, { createContext, useContext, ReactNode } from 'react';
import { cn } from '../../utils/cn';

interface Tab {
  id: string;
  label: string;
}

type TabsContextValue = {
  value: string;
  onValueChange: (value: string) => void;
};

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

interface TabsProps {
  tabs?: Tab[];
  activeTab?: string;
  onChange?: (tabId: string) => void;
  // For children-based API
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children?: ReactNode;
  className?: string;
}

export function Tabs({ 
  tabs, 
  activeTab, 
  onChange, 
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className
}: TabsProps) {
  // Handle internal state for uncontrolled components
  const [internalValue, setInternalValue] = React.useState(defaultValue || '');

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  // Handle value changes
  const handleValueChange = React.useCallback((newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  }, [isControlled, onValueChange]);

  // Support for the legacy API
  React.useEffect(() => {
    if (activeTab !== undefined) {
      handleValueChange(activeTab);
    }
  }, [activeTab, handleValueChange]);

  const handleLegacyChange = React.useCallback((tabId: string) => {
    onChange?.(tabId);
    handleValueChange(tabId);
  }, [onChange, handleValueChange]);

  const contextValue = React.useMemo(() => ({
    value,
    onValueChange: handleValueChange,
  }), [value, handleValueChange]);

  // Render legacy UI if tabs prop is provided
  if (tabs && tabs.length > 0) {
    return (
      <TabsContext.Provider value={contextValue}>
        <div className={cn("border-b", className)}>
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleLegacyChange(tab.id)}
                className={cn(
                  'py-2 border-b-2 font-medium text-sm transition-colors',
                  value === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </TabsContext.Provider>
    );
  }

  // Render new UI with children
  return (
    <TabsContext.Provider value={contextValue}>
      <div className={className}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// Utility hook to access TabsContext
const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs compound components must be used within a Tabs component');
  }
  return context;
};

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={cn("flex border-b space-x-8", className)}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const { value: selectedValue, onValueChange } = useTabsContext();
  const isActive = selectedValue === value;

  return (
    <button
      onClick={() => onValueChange(value)}
      className={cn(
        'py-2 border-b-2 font-medium text-sm transition-colors',
        isActive
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
        className
      )}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { value: selectedValue } = useTabsContext();
  
  if (selectedValue !== value) {
    return null;
  }

  return (
    <div className={className}>
      {children}
    </div>
  );
}